import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTimeWAT } from "@/lib/format";
import { toast } from "sonner";
import { FileText, AlertTriangle, ArrowLeftRight, Clock } from "lucide-react";

const TYPES = [
  { key: "DAR", label: "Daily Activity", icon: FileText },
  { key: "INCIDENT", label: "Incident", icon: AlertTriangle, danger: true },
  { key: "PASSDOWN", label: "Passdown", icon: ArrowLeftRight },
  { key: "HOURLY", label: "Hourly check", icon: Clock },
];

export const GuardReports = () => {
  const { user } = useAuth();
  const [type, setType] = useState(null);
  const [text, setText] = useState("");
  const [incidentCat, setIncidentCat] = useState("theft");
  const [list, setList] = useState([]);
  const [activeSite, setActiveSite] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/reports");
    setList(r.data.filter(x => x.guard_id === user.guard_id));
  }, [user.guard_id]);

  useEffect(() => {
    load();
    api.get("/clockins?active_only=true").then(r => {
      const mine = r.data[0];
      setActiveSite(mine?.site_id || null);
    });
  }, [load]);

  const submit = async () => {
    if (!text.trim()) return toast.error("Type your report");
    try {
      const payload = {
        type, site_id: activeSite, text,
        photos: [],
        lat: 6.45, lng: 3.4,
        incident_category: type === "INCIDENT" ? incidentCat : null,
      };
      await api.post("/reports", payload);
      toast.success(`${type} report submitted`);
      setText(""); setType(null);
      load();
    } catch { toast.error("Failed"); }
  };

  if (type) {
    return (
      <div className="space-y-3">
        <div className="text-muted" style={{ fontSize: 13 }}>{TYPES.find(t => t.key === type)?.label}</div>
        {type === "INCIDENT" && (
          <select value={incidentCat} onChange={(e) => setIncidentCat(e.target.value)} className="s-input">
            {["theft","break-in","threat","injury","suspicious-activity","trespassing"].map(c => <option key={c}>{c}</option>)}
          </select>
        )}
        <textarea value={text} onChange={(e) => setText(e.target.value)}
                  className="s-input" rows={8} placeholder="Type your report..." data-testid="report-text" />
        <div className="flex gap-2">
          <button onClick={() => setType(null)} className="btn-outline flex-1 tap-target">Cancel</button>
          <button onClick={submit} className="btn-primary flex-1 tap-target" data-testid="submit-report">Submit</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {TYPES.map(t => (
          <button key={t.key} onClick={() => setType(t.key)}
                  className="s-card tap-target flex flex-col items-center justify-center"
                  style={{ padding: 20, minHeight: 110 }} data-testid={`new-${t.key}`}>
            <t.icon size={26} color={t.danger ? "#E63946" : "#2ECAC8"} />
            <div style={{ fontSize: 14, color: "#fff", marginTop: 10 }}>{t.label}</div>
          </button>
        ))}
      </div>

      <div className="h-section mt-2">My recent reports</div>
      {list.slice(0, 20).map(r => (
        <div key={r.id} className="s-card" data-testid={`my-report-${r.id}`}>
          <div className="flex justify-between items-start mb-2">
            <StatusBadge status={r.type} />
            <span className="text-muted mono" style={{ fontSize: 11 }}>{formatDateTimeWAT(r.created_at)}</span>
          </div>
          <div style={{ fontSize: 13, color: "#fff", lineHeight: 1.5 }}>{r.text}</div>
        </div>
      ))}
    </div>
  );
};
